/* Minimal axios wrapper + auth context for the store portal.
   Stored under a separate localStorage key so it never collides
   with the admin/employee JWT. */
import axios from 'axios';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const STORE_API = `${BACKEND}/api`;
const STORE_TOKEN_KEY = 'store_token';
const STORE_PROFILE_KEY = 'store_profile';

export function storeLogin(franchiseId, password) {
  return axios.post(`${STORE_API}/store/auth/login`, {
    franchise_id: franchiseId,
    password,
  }).then((res) => {
    localStorage.setItem(STORE_TOKEN_KEY, res.data.token);
    localStorage.setItem(STORE_PROFILE_KEY, JSON.stringify(res.data.store));
    return res.data;
  });
}

export function storeLogout() {
  localStorage.removeItem(STORE_TOKEN_KEY);
  localStorage.removeItem(STORE_PROFILE_KEY);
  localStorage.removeItem('store_cart');
}

export function getStoreToken() {
  return localStorage.getItem(STORE_TOKEN_KEY);
}

export function getStoreProfile() {
  const raw = localStorage.getItem(STORE_PROFILE_KEY);
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
}

export function saveStoreProfile(profile) {
  localStorage.setItem(STORE_PROFILE_KEY, JSON.stringify(profile));
}

/** Authenticated axios instance for store-side calls.
 *  Auto-attaches the store JWT and redirects to /store/login on 401. */
export const storeApi = axios.create({ baseURL: STORE_API });
storeApi.interceptors.request.use((config) => {
  const tok = getStoreToken();
  if (tok) config.headers.Authorization = `Bearer ${tok}`;
  return config;
});
storeApi.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response && err.response.status === 401) {
      storeLogout();
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/store/login')) {
        window.location.href = '/store/login';
      }
    }
    return Promise.reject(err);
  },
);

// ─── Cart utilities (Feb 2026: backed by server collection store_carts) ──
// Backwards-compat re-exports — existing pages still call `getCart()` and
// `saveCart()` synchronously. The new typed helpers (cartAdd / cartSetQty /
// cartRemove / cartClear / hydrateCartFromServer) live in `cartStore.js`.
export {
  getCart, saveCart, cartCount,
  cartAdd, cartSetQty, cartRemove, cartClear, cartReplace,
  hydrateCartFromServer,
} from './cartStore';

// Minimum order qty per product. PCS units have a 3-piece minimum.
// Non-PCS units (KG, GRAMS, LITRE…) can be ordered from 1.
export function minOrderQty(product) {
  const unit = (product?.unit || product?.measurement_unit || '').toString().toUpperCase();
  return unit === 'PCS' ? 3 : 1;
}
